'use client';

import { useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import NextImage from 'next/image';
import { cn } from '@/app/lib/utils/utils';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/components/providers/context/locale-context';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

export default function NewsletterHover() {
    const locale = useLocale();
    const containerRef = useRef<HTMLDivElement>(null);
    const topTextRef = useRef<HTMLDivElement>(null);
    const bottomTextRef = useRef<HTMLDivElement>(null);
    const catImageRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const textAreaRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const animationStateRef = useRef<'idle' | 'playing' | 'reversing'>('idle');

    const { t } = useTranslation('newsletter');

    useGSAP(() => {
        let mm = gsap.matchMedia();

        mm.add('(min-width: 735px)', () => {
            // 确保所有ref都存在
            if (
                !topTextRef.current ||
                !bottomTextRef.current ||
                !catImageRef.current ||
                !buttonRef.current ||
                !textAreaRef.current
            ) {
                console.warn('Some refs are not ready');
                return;
            }

            // 设置初始状态
            gsap.set(buttonRef.current, {
                y: 90,
                opacity: 0,
                rotation: -10,
            });

            // 创建时间线
            const hoverTl = gsap.timeline({
                paused: true,
                onComplete: () => {
                    animationStateRef.current = 'idle';
                    console.log('Animation completed');
                },
                onReverseComplete: () => {
                    animationStateRef.current = 'idle';
                    console.log('Animation reverse completed');
                },
            });

            // 顶部文字动画：向上移动并旋转淡出
            hoverTl.to(
                topTextRef.current,
                {
                    y: -30,
                    opacity: 0.3,
                    rotation: -5,
                    scaleY: 0.5,
                    duration: 0.4,
                },
                0,
            );

            // 底部文字动画：向下移动并旋转淡出
            hoverTl.to(
                bottomTextRef.current,
                {
                    y: 30,
                    opacity: 0.3,
                    rotation: 5,
                    scaleY: 0.5,
                    duration: 0.4,
                },
                0,
            );

            // 猫头图标缩小消失
            hoverTl.to(
                catImageRef.current,
                {
                    scale: 0,
                    opacity: 0,
                    duration: 0.3,
                },
                0.1,
            );

            // 按钮从下往上出现并旋转
            hoverTl.to(
                buttonRef.current,
                {
                    y: 0,
                    opacity: 1,
                    rotation: 0,
                    duration: 0.3,
                },
                0.1,
            );

            // 优化的鼠标悬停处理
            const handleMouseEnter = () => {
                console.log('Mouse enter triggered');

                // 清除可能存在的延迟隐藏
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }

                // 只有在非播放状态时才播放动画
                if (animationStateRef.current !== 'playing') {
                    console.log('Playing animation');
                    setIsHovered(true);
                    animationStateRef.current = 'playing';
                    hoverTl.play();
                }
            };

            const handleMouseLeave = () => {
                console.log('Mouse leave triggered');

                // 使用延迟来避免频繁切换
                timeoutRef.current = setTimeout(() => {
                    // 只有在非反向播放状态时才反向播放动画
                    if (animationStateRef.current !== 'reversing') {
                        console.log('Reversing animation');
                        setIsHovered(false);
                        animationStateRef.current = 'reversing';
                        hoverTl.reverse();
                    }
                    timeoutRef.current = null;
                }, 100); // 100ms延迟，避免快速进出时的抖动
            };

            const textArea = textAreaRef.current;
            const button = buttonRef.current;

            if (textArea && button) {
                console.log('Adding event listeners');

                // 文字区域事件
                textArea.addEventListener('mouseenter', handleMouseEnter);
                textArea.addEventListener('mouseleave', handleMouseLeave);

                // 按钮事件 - 保持hover状态
                button.addEventListener('mouseenter', handleMouseEnter);
                button.addEventListener('mouseleave', handleMouseLeave);

                return () => {
                    console.log('Cleaning up event listeners');
                    // 清理timeout
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }

                    textArea.removeEventListener(
                        'mouseenter',
                        handleMouseEnter,
                    );
                    textArea.removeEventListener(
                        'mouseleave',
                        handleMouseLeave,
                    );
                    button.removeEventListener('mouseenter', handleMouseEnter);
                    button.removeEventListener('mouseleave', handleMouseLeave);
                };
            }
        });

        return () => mm.revert();
    }, []);

    return (
        <div
            ref={containerRef}
            className={cn(
                'tablet:max-w-[58vw] desktop:max-w-[800px] relative flex h-auto w-[58vw] max-w-[280px] select-none items-center justify-center',
                'tablet:pt-[120px] tablet:pt-[160px] tablet:pb-[160px] desktop:pb-[180px] pb-[60px] pt-[60px]',
                {
                    "font-['Satoshi'] text-[54px]": locale === 'en',
                    "font-['NotoSerifTC'] text-[50px]": locale === 'zh',
                },
            )}
        >
            {/* 文字区域 */}
            <div
                ref={textAreaRef}
                className="tablet:flex desktop:gap-[24px] hidden w-full flex-col items-center justify-center gap-[16px]"
            >
                {/* 顶部文字 */}
                <div ref={topTextRef} className="w-full">
                    <NextImage
                        src={getStaticAssetUrl(
                            locale === 'en'
                                ? '/newsletter-p1-en.svg'
                                : '/newsletter-p1-zh.svg',
                        )}
                        alt="p1"
                        width={730}
                        height={52}
                        style={{ width: '100%', height: 'auto' }}
                    />
                </div>
                {/* 底部文字 */}
                <div ref={bottomTextRef} className="w-full">
                    <NextImage
                        src={getStaticAssetUrl(
                            locale === 'en'
                                ? '/newsletter-p2-en.svg'
                                : '/newsletter-p2-zh.svg',
                        )}
                        alt="p2"
                        width={730}
                        height={52}
                        style={{ width: '100%', height: 'auto' }}
                    />
                </div>
            </div>
            {/* 猫头区域 */}
            <div className="tablet:flex pointer-events-none absolute bottom-0 left-0 right-0 top-0 hidden flex-col items-center justify-center">
                <div ref={catImageRef} className="w-[15%]">
                    <NextImage
                        src={getStaticAssetUrl('/cat.webp')}
                        alt="cat"
                        width={137}
                        height={109}
                    />
                </div>
            </div>
            {/* Newsletter 订阅按钮 */}
            <div
                className={cn(
                    'tablet:flex pointer-events-none absolute bottom-0 left-0 right-0 top-0 hidden cursor-pointer flex-col items-center justify-center',
                )}
            >
                <button
                    ref={buttonRef}
                    className={cn(
                        'j-t2 pointer-events-auto cursor-pointer rounded-[60px] bg-[#0A0708]',
                        'tablet:pl-[24px] tablet:pr-[12px] pl-[16px] pr-[8px]',
                        'text-white shadow-lg transition-colors duration-100',
                        'hover:bg-gray-900 hover:shadow-xl',
                        'tablet:gap-[12px] flex items-center gap-[8px]',
                        'tablet:h-[56px] h-[42px]',
                        'box-border',
                    )}
                    onClick={() => {
                        if (locale === 'zh') {
                            // 跳转到订阅页面
                            window.location.href = `/${locale}/subscribe`;
                        } else {
                            window.open(
                                'https://jojojoinai.substack.com',
                                '_blank',
                            );
                        }
                    }}
                >
                    <span>{t('subscribeButton')}</span>
                    <NextImage
                        src={getStaticAssetUrl('/icons/arrow.webp')}
                        alt="arrow"
                        width={21}
                        height={21}
                        className="h-[21px] w-[21px]"
                    />
                </button>
            </div>

            {/* 移动端布局 */}
            <div className="tablet:hidden flex w-full flex-col items-center justify-center gap-[32px]">
                <NextImage
                    src={getStaticAssetUrl(
                        locale === 'en'
                            ? '/newsletter-mobile-en.svg'
                            : '/newsletter-mobile-zh.svg',
                    )}
                    alt="newsletter"
                    width={280}
                    height={100}
                    style={{ width: '100%', height: 'auto' }}
                />
                <button
                    className={cn(
                        'j-t2 cursor-pointer rounded-[60px] bg-[#0A0708]',
                        'pl-[16px] pr-[8px]',
                        'text-white shadow-lg transition-colors duration-100',
                        'hover:bg-gray-900 hover:shadow-xl',
                        'flex items-center gap-[8px]',
                        'h-[42px]',
                        'box-border',
                    )}
                    onClick={() => {
                        if (locale === 'zh') {
                            // 跳转到订阅页面
                            window.location.href = `/${locale}/subscribe`;
                        } else {
                            window.open(
                                'https://jojojoinai.substack.com',
                                '_blank',
                            );
                        }
                    }}
                >
                    <span>{t('subscribeButton')}</span>
                    <NextImage
                        src={getStaticAssetUrl('/icons/arrow.webp')}
                        alt="arrow"
                        width={21}
                        height={21}
                        className="h-[21px] w-[21px]"
                    />
                </button>
            </div>
        </div>
    );
}
